/**
 * PausedMembershipBanner — banner-center source (§7.10 pause freeze, owner
 * decision 5, 2026-08-16).
 *
 * When the signed-in member's OWN membership in the ACTIVE workspace is
 * suspended, this says so plainly: their access is FROZEN, not ended — org
 * threads stay visible read-only (nothing disappears), and posting/switching
 * are blocked server-side until an admin resumes them. Rides the one global
 * banner band like every other status notice; not dismissible (a frozen
 * member must always know why the workspace is read-only).
 *
 * Membership status arrives via useRbac (member-doc read / callable field) —
 * null/unknown renders nothing, so this can never false-fire while loading.
 */

import React from 'react';
import { useRbac } from '../../hooks/useRbac';
import { useBannerSurface } from './bannerSurface';
import { useRegisterBanner, BannerDescriptor } from './GlobalBannerCenter';
import type { BannerNotice } from './bannerNotice';

/**
 * The words, one table for both designs' bands (./bannerNotice.ts). The phone line (2026-09-13): this one states an
 * ACCESS FACT, so what survives is the fact and its consequence — paused, read-only. Who resumes it and that your own
 * organization is unaffected are the two clauses a tap reveals; neither changes what the reader can do right now.
 */
// copy: membership_paused_band
const PAUSED_COPY = {
  line:
    'your membership in this organization is paused — threads stay visible but are read-only until an admin resumes your access. your own organization is unaffected: switch workspaces in the top-right menu.',
  lineShort: 'your access here is paused — read-only.',
};

const NOTICE: BannerNotice = {
  words: PAUSED_COPY.line,
  shortWords: PAUSED_COPY.lineShort,
  wordsMarker: 'data-paused-membership-banner',
};

export const PausedMembershipBanner: React.FC = () => {
  const onAppSurface = useBannerSurface();
  const { membershipStatus, loading, isOwner } = useRbac();

  const paused = !loading && !isOwner && membershipStatus === 'suspended';

  const banner: BannerDescriptor | null =
    onAppSurface && paused
      ? {
        id: 'paused-membership',
        tone: 'warn',
        dismissible: false,
        notice: NOTICE,
        shortContent: (
          <span data-paused-membership-banner="">{PAUSED_COPY.lineShort}</span>
        ),
        content: (
          <span data-paused-membership-banner="">{PAUSED_COPY.line}</span>
        ),
      }
      : null;

  useRegisterBanner(banner);
  return null;
};

export default PausedMembershipBanner;
